import { useCallback, useEffect, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import {
  FormProvider,
  useForm,
  useFormContext,
} from '@/components/react-hook-form';
import { CoverSlideConfigTitleTextSize } from '@/modules/slideEditor/coverSlide/CoverSlide.types';
import {
  TenantInformationDocument,
  TenantInformationQuery,
} from '@/modules/tenant/graphql/TenantInformation.generated';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';
import { useSubBrand } from '@/modules/tenant/TenantDetailsContext/useSubBrand';

// The legacy logoSize/logoPosition controls are gone; their @deprecated
// BE counterparts are now cleared on every save so the new
// logoPercentScale / useDefaultLogoPosition / logoOriginX/Y fields are
// the sole source of truth.
import { CoverSlideConfigPreview } from './CoverSlideConfigPreview';
import { DividerSlideConfigPreview } from './DividerSlideConfigPreview';
import {
  useUpdateCoverSlideConfigMutation,
  useUpdateDividerSlideConfigMutation,
} from './graphql/SlideConfig.generated';
import {
  formToSlideDesign,
  slideConfigDefaultValues,
  titleSizePtFromBucket,
} from './SlideConfigForm.constants';
import {
  CoverSlideConfigBodyTextAlignment,
  PT_PER_REM,
  SlideConfigFormShape,
  SlideConfigVariant,
} from './SlideConfigForm.types';
import { SlideConfigFormFields } from './SlideConfigFormFields';

type SlideConfigFragment = TenantInformationQuery['coverSlideConfig'];

const VARIANT_CONFIG: Record<
  SlideConfigVariant,
  { heading: string; subheading: string }
> = {
  [SlideConfigVariant.COVER]: {
    heading: 'Cover slide',
    subheading:
      'Customize the appearance of the cover slide used in presentations.',
  },
  [SlideConfigVariant.DIVIDER]: {
    heading: 'Divider slide',
    subheading:
      'Customize the appearance of the divider slide used in presentations.',
  },
};

/** Convert a GraphQL config fragment into form values. */
function toFormValues(config: SlideConfigFragment): SlideConfigFormShape {
  if (!config) return slideConfigDefaultValues;

  return {
    useDarkLogo: config.useDarkLogo ?? false,
    footerColor: config.footerColor ?? slideConfigDefaultValues.footerColor,
    backgroundGradientEndColor: config.backgroundGradientEndColor ?? '',
    backgroundGradientAngle: String(config.backgroundGradientAngle ?? 90),
    backgroundOverlayPath: config.backgroundOverlayPath ?? '',
    backgroundOverlayAlpha: String(
      (config.backgroundOverlayAlpha ?? 0.2) * 100
    ),
    backgroundOverlayPercentScale: String(
      config.backgroundOverlayPercentScale ?? 100
    ),
    backgroundOverlayOriginX: String(config.backgroundOverlayOriginX ?? 50),
    backgroundOverlayOriginY: String(config.backgroundOverlayOriginY ?? 50),
    // Color fields default to the canonical white/black hex so the
    // input is always populated with the actual value being rendered
    // — no grey placeholder state.
    backgroundColor:
      config.backgroundColor ?? slideConfigDefaultValues.backgroundColor,
    textColor: config.textColor ?? slideConfigDefaultValues.textColor,
    bodyTextColor:
      config.bodyTextColor ?? slideConfigDefaultValues.bodyTextColor,
    // Null on the BE means "not set" — fall back to the default
    // left alignment.
    bodyTextAlignment:
      config.bodyTextAlignment ?? slideConfigDefaultValues.bodyTextAlignment,
    backgroundImageUrl: config.backgroundImagePath ?? '',
    // BE stores alpha as a 0-1 float; form uses a 0-100 percentage as
    // a string. Falls back to the canonical default when the BE is null
    // so the input is always populated — same pattern as the color
    // inputs above.
    backgroundAlpha:
      config.backgroundAlpha != null
        ? String(Math.round(config.backgroundAlpha * 100))
        : slideConfigDefaultValues.backgroundAlpha,
    titleTextSize: config.titleTextSize ?? CoverSlideConfigTitleTextSize.Medium,
    // BE stores rem; form stores pt (the unit users see). The slider
    // initializes to the existing rem value when present; otherwise
    // we seed it from the legacy `titleTextSize` bucket so a
    // pre-migration tenant configured to "Large" / "Small" doesn't
    // silently snap to Medium on first open. Round to 1 decimal
    // (matching the 0.1 slider step) so the pt→rem→pt round trip
    // doesn't accumulate float drift like "27.099999999999998".
    titleSizePt:
      config.titleSizeRem != null
        ? (config.titleSizeRem * PT_PER_REM).toFixed(1)
        : titleSizePtFromBucket(
            config.titleTextSize ?? CoverSlideConfigTitleTextSize.Medium
          ),
    includeFooter: config.includeFooter ?? true,
    // Default to true (use cover values) when not yet saved
    inheritCover: config.dividerInheritCover ?? true,
    // "Show logo" is the inverse of the BE `hideLogo` flag. Default
    // to "show" when nothing has been saved.
    showLogo: !(config.hideLogo ?? false),
    backgroundImageOriginX: config.backgroundImageOriginX ?? null,
    backgroundImageOriginY: config.backgroundImageOriginY ?? null,
    textOriginX: config.textOriginX ?? null,
    textOriginY: config.textOriginY ?? null,
    textWidth:
      config.textWidth != null ? String(Math.round(config.textWidth)) : '',
    // Percent fields use the same string convention as backgroundAlpha,
    // and fall back to canonical 100% defaults when the BE is null so
    // the input is always populated.
    backgroundImagePercentScale:
      config.backgroundImagePercentScale != null
        ? String(Math.round(config.backgroundImagePercentScale))
        : slideConfigDefaultValues.backgroundImagePercentScale,
    logoPercentScale:
      config.logoPercentScale != null
        ? String(Math.round(config.logoPercentScale))
        : slideConfigDefaultValues.logoPercentScale,
    // Default to true when nothing has been saved — matches the visual
    // default (logo anchored to the title block).
    useDefaultLogoPosition: config.useDefaultLogoPosition ?? true,
    logoOriginX: config.logoOriginX ?? null,
    logoOriginY: config.logoOriginY ?? null,
  };
}

/**
 * Fallback values for the slider-style inputs. Their form values are
 * stored as empty strings when unset; these numbers back the fallback
 * rendering. Color inputs intentionally aren't here — they're populated
 * with real hex values via `slideConfigDefaultValues`.
 */
const PLACEHOLDERS = {
  backgroundAlpha: '100',
  backgroundImagePercentScale: '100',
  logoPercentScale: '100',
};

export type SlideConfigPlaceholders = typeof PLACEHOLDERS;

/**
 * Returns true if a string-stored percent form value is explicitly set.
 * Treats '' as "not set"; '0' is a valid value (matters for alpha).
 */
function hasPercentValue(value: string): boolean {
  return value !== '';
}

/** Convert form values into the GraphQL mutation input. */
function toMutationInput(
  values: SlideConfigFormShape,
  variant: SlideConfigVariant
) {
  const isDivider = variant === SlideConfigVariant.DIVIDER;
  const hasBgAlpha = hasPercentValue(values.backgroundAlpha);
  const hasBgImageScale = hasPercentValue(values.backgroundImagePercentScale);
  const hasLogoScale = hasPercentValue(values.logoPercentScale);
  const hasTitleSizePt = hasPercentValue(values.titleSizePt);
  const hasTextWidth = hasPercentValue(values.textWidth);
  // Left is the default — clear the BE field to null in that case so an
  // unconfigured tenant stays "not set" rather than persisting the
  // default explicitly. Center/right are stored.
  const isDefaultBodyAlignment =
    values.bodyTextAlignment === CoverSlideConfigBodyTextAlignment.LeftAligned;
  const slideDesign = formToSlideDesign(values);

  return {
    useDarkLogo: values.useDarkLogo,
    footerColor: values.footerColor || null,
    clearFooterColor: !values.footerColor,
    backgroundGradientEndColor: values.backgroundGradientEndColor || null,
    clearBackgroundGradientEndColor: !values.backgroundGradientEndColor,
    backgroundGradientAngle: slideDesign.backgroundGradientAngle,
    backgroundOverlayPath: values.backgroundOverlayPath || null,
    clearBackgroundOverlayPath: !values.backgroundOverlayPath,
    backgroundOverlayAlpha: slideDesign.backgroundOverlayAlpha,
    backgroundOverlayPercentScale: slideDesign.backgroundOverlayPercentScale,
    backgroundOverlayOriginX: slideDesign.backgroundOverlayOriginX,
    backgroundOverlayOriginY: slideDesign.backgroundOverlayOriginY,
    backgroundColor: values.backgroundColor || null,
    textColor: values.textColor || null,
    bodyTextColor: values.bodyTextColor || null,
    bodyTextAlignment: isDefaultBodyAlignment ? null : values.bodyTextAlignment,
    backgroundImagePath: values.backgroundImageUrl || null,
    // Convert percentage back to 0-1 float for the BE.
    backgroundAlpha: hasBgAlpha ? Number(values.backgroundAlpha) / 100 : null,
    // T1-5959 origins + scales — sent as integers in slide-coordinate
    // pixels (origins) or 0–100-style percent (scales). Empty/null gets
    // explicitly cleared so the BE state matches what the form shows.
    backgroundImageOriginX: values.backgroundImageOriginX,
    backgroundImageOriginY: values.backgroundImageOriginY,
    backgroundImagePercentScale: hasBgImageScale
      ? Number(values.backgroundImagePercentScale)
      : null,
    textOriginX: values.textOriginX,
    textOriginY: values.textOriginY,
    textWidth: hasTextWidth ? Number(values.textWidth) : null,
    logoOriginX: values.logoOriginX,
    logoOriginY: values.logoOriginY,
    logoPercentScale: hasLogoScale ? Number(values.logoPercentScale) : null,
    useDefaultLogoPosition: values.useDefaultLogoPosition,
    hideLogo: !values.showLogo,
    titleTextSize: values.titleTextSize,
    // BE field is rem; form holds pt. Convert at the boundary.
    titleSizeRem: hasTitleSizePt
      ? Number(values.titleSizePt) / PT_PER_REM
      : null,
    includeFooter: values.includeFooter,
    // Clear nullable fields when they're empty.
    clearBackgroundColor: !values.backgroundColor,
    clearTextColor: !values.textColor,
    clearBodyTextColor: !values.bodyTextColor,
    clearBodyTextAlignment: isDefaultBodyAlignment,
    clearBackgroundImagePath: !values.backgroundImageUrl,
    clearBackgroundAlpha: !hasBgAlpha,
    clearBackgroundImageOriginX: values.backgroundImageOriginX === null,
    clearBackgroundImageOriginY: values.backgroundImageOriginY === null,
    clearBackgroundImagePercentScale: !hasBgImageScale,
    clearTextOriginX: values.textOriginX === null,
    clearTextOriginY: values.textOriginY === null,
    clearTextWidth: !hasTextWidth,
    clearLogoOriginX: values.logoOriginX === null,
    clearLogoOriginY: values.logoOriginY === null,
    clearLogoPercentScale: !hasLogoScale,
    clearTitleSizeRem: !hasTitleSizePt,
    // The form replaces logoSize / logoPosition with the new model;
    // wipe the deprecated BE fields so nothing legacy can leak through.
    clearLogoSize: true,
    clearLogoPosition: true,
    ...(isDivider && {
      dividerInheritCover: values.inheritCover,
    }),
  };
}

interface SlideConfigFormProps {
  variant: SlideConfigVariant;
  /**
   * When set, the form edits the sub-brand's slide config and the
   * mutation targets that sub-brand. When null, the form edits the
   * tenant-default config.
   */
  selectedSubBrandId: string | null;
  /**
   * Reports whether the form has unsaved changes so the parent can
   * prompt before a tab switch.
   */
  onDirtyChange?: (dirty: boolean) => void;
  /**
   * Reports whether a save is in flight so the parent can hard-block
   * tab / sub-brand switches until the mutation settles.
   */
  onSubmittingChange?: (submitting: boolean) => void;
}

function SlideConfigFormInner({
  variant,
  onSubmit,
}: Pick<SlideConfigFormProps, 'variant'> & {
  onSubmit: () => void;
}) {
  const { watch } = useFormContext<SlideConfigFormShape>();
  const values = watch();
  const { heading, subheading } = VARIANT_CONFIG[variant];

  const preview =
    variant === SlideConfigVariant.COVER ? (
      <CoverSlideConfigPreview {...values} />
    ) : (
      <DividerSlideConfigPreview {...values} />
    );

  return (
    <SlideConfigFormFields
      heading={heading}
      subheading={subheading}
      preview={preview}
      placeholders={PLACEHOLDERS}
      onSubmit={onSubmit}
      variant={variant}
    />
  );
}

export function SlideConfigForm({
  variant,
  selectedSubBrandId,
  onDirtyChange,
  onSubmittingChange,
}: SlideConfigFormProps) {
  const { createErrorFeedback, createSuccessFeedback } = useFeedback();
  const { coverSlideConfig, dividerSlideConfig, subBrandsById } =
    useTenantDetailsContext();

  // Publish the selected sub-brand to context so the live preview's
  // `useMergedSlideDesign` resolves against the sub-brand's configs.
  // The admin page isn't wrapped in CustomerThemeProvider, so this has
  // no theming side effects; it auto-clears when the form unmounts.
  useSubBrand(selectedSubBrandId);

  const isCover = variant === SlideConfigVariant.COVER;
  const subBrand = selectedSubBrandId
    ? subBrandsById[selectedSubBrandId]
    : undefined;
  // Resolve both the cover and divider config for the active brand context
  // so the divider form can fall back to cover values when inheriting or
  // when no divider config exists yet.
  const effectiveCoverConfig: SlideConfigFragment = subBrand
    ? (subBrand.presentationCoverSlideConfig ?? null)
    : coverSlideConfig;
  const effectiveDividerConfig: SlideConfigFragment = subBrand
    ? (subBrand.presentationDividerSlideConfig ?? null)
    : dividerSlideConfig;
  const existingConfig: SlideConfigFragment = isCover
    ? effectiveCoverConfig
    : effectiveDividerConfig;

  // Compute the form's initial values from BE data captured at mount.
  // Without this, the form initializes with `slideConfigDefaultValues`
  // (which has `inheritCover: true`) and the slide preview's first
  // render uses that — making `useMergedSlideDesign` resolve to the
  // cover. The reset effect below then corrects the form state, but
  // we'd rather not flash the wrong values into the preview at all,
  // especially on tab switch where the user actively notices it.
  const [initialFormValues] = useState<SlideConfigFormShape>(() => {
    if (isCover) return toFormValues(existingConfig);
    const initialInherit = effectiveDividerConfig?.dividerInheritCover ?? true;
    // Inlined from `resolveDividerVisualSource` — that callback isn't
    // available yet at this point in render, and we only need the
    // mount-time resolution here.
    const visualSource: SlideConfigFragment =
      initialInherit || !effectiveDividerConfig
        ? effectiveCoverConfig
        : effectiveDividerConfig;
    return { ...toFormValues(visualSource), inheritCover: initialInherit };
  });

  const formMethods = useForm<SlideConfigFormShape>({
    defaultValues: initialFormValues,
    mode: 'onBlur',
  });

  const { reset, handleSubmit, control, setValue, formState } = formMethods;
  const { isDirty, isSubmitting } = formState;

  // The post-save refetch updates `existingConfig`, which triggers the
  // reset effect below and clears `isDirty`. A failed save doesn't
  // refetch, so the form stays dirty until the user retries or resets.
  // `!isSubmitting` just suppresses the prompt during the in-flight
  // window of an active save.
  const isEffectiveDirty = isDirty && !isSubmitting;

  useEffect(() => {
    onDirtyChange?.(isEffectiveDirty);
  }, [isEffectiveDirty, onDirtyChange]);

  useEffect(() => {
    return () => onDirtyChange?.(false);
  }, [onDirtyChange]);

  useEffect(() => {
    onSubmittingChange?.(isSubmitting);
  }, [isSubmitting, onSubmittingChange]);

  useEffect(() => {
    return () => onSubmittingChange?.(false);
  }, [onSubmittingChange]);

  /**
   * For the divider variant, the form's visual fields are sourced from
   * the cover config when:
   *   - the user is inheriting cover styles (checkbox checked), or
   *   - no divider config exists for the brand yet.
   * Otherwise, they're sourced from the saved divider config.
   *
   * This keeps the visible form values aligned with what `useMergedSlideDesign`
   * resolves for rendering, and gives the user a sensible starting point
   * (cover values) the first time they uncheck the inherit box.
   */
  const resolveDividerVisualSource = useCallback(
    (inheritCover: boolean): SlideConfigFragment =>
      inheritCover || !effectiveDividerConfig
        ? effectiveCoverConfig
        : effectiveDividerConfig,
    [effectiveCoverConfig, effectiveDividerConfig]
  );

  // Single effect that handles both initial config-driven population and
  // live response to inheritCover toggles. Splitting these into two effects
  // races: the toggle effect would fire on mount with the form's stale
  // default `inheritCover: true` and overwrite the values the init effect
  // just reset, leaving a frame where fields are enabled but show cover
  // values when the saved divider has `dividerInheritCover: false`.
  //
  // The single-effect form has its own subtle race: after the config-change
  // branch `reset`s `inheritCover` to the saved value, `watchedInherit`
  // updates on the next render and re-fires this effect. Without tracking
  // the last-seen inherit, that re-fire would be mistaken for a user
  // toggle and overwrite the just-loaded divider values with cover ones.
  // We compare `watchedInherit` against the last value we ourselves set
  // and bail when they match.
  const watchedInherit = useWatch({ control, name: 'inheritCover' });
  // Seed with the mount-time snapshot so the first effect run sees
  // "configs unchanged" and bails. Otherwise we'd reset the form to
  // the values we already initialized it with, which (1) is redundant
  // and (2) can flip `watchedInherit` and re-fire the effect into the
  // toggle branch.
  const lastSeenRef = useRef<{
    cover: SlideConfigFragment;
    divider: SlideConfigFragment;
    inherit: boolean;
  } | null>(
    isCover
      ? null
      : {
          cover: effectiveCoverConfig,
          divider: effectiveDividerConfig,
          inherit: initialFormValues.inheritCover,
        }
  );

  useEffect(() => {
    if (isCover) {
      reset(toFormValues(existingConfig));
      lastSeenRef.current = null;
      return;
    }
    // Detect config change (mount, sub-brand switch, refetch) vs an in-place
    // toggle of the inheritCover checkbox.
    const isConfigChange =
      !lastSeenRef.current ||
      lastSeenRef.current.cover !== effectiveCoverConfig ||
      lastSeenRef.current.divider !== effectiveDividerConfig;

    if (isConfigChange) {
      // Initial / config-change: take inheritCover from the saved divider
      // config and pick the visual source per the rule.
      const initialInherit =
        effectiveDividerConfig?.dividerInheritCover ?? true;
      const visualSource = resolveDividerVisualSource(initialInherit);
      reset({
        ...toFormValues(visualSource),
        inheritCover: initialInherit,
      });
      lastSeenRef.current = {
        cover: effectiveCoverConfig,
        divider: effectiveDividerConfig,
        inherit: initialInherit,
      };
      return;
    }

    // Configs unchanged. Distinguish a genuine user toggle from the
    // post-reset re-render that flips `watchedInherit` to the value we
    // just set above. `isConfigChange === false` proves `current` is
    // non-null but TS can't narrow through the OR — assert.
    const lastSeen = lastSeenRef.current!;
    if (lastSeen.inherit === watchedInherit) return;
    lastSeen.inherit = watchedInherit;

    // User toggled the checkbox in place — keep their inheritCover choice
    // and seed the visual fields from the cover config. We always pull
    // cover values here (regardless of toggle direction) so unchecking
    // the box gives the user a fresh cover-derived starting point to
    // customize rather than re-surfacing potentially stale divider values.
    const values = toFormValues(effectiveCoverConfig);
    setValue('useDarkLogo', values.useDarkLogo);
    setValue('footerColor', values.footerColor);
    setValue('backgroundGradientEndColor', values.backgroundGradientEndColor);
    setValue('backgroundGradientAngle', values.backgroundGradientAngle);
    setValue('backgroundOverlayPath', values.backgroundOverlayPath);
    setValue('backgroundOverlayAlpha', values.backgroundOverlayAlpha);
    setValue(
      'backgroundOverlayPercentScale',
      values.backgroundOverlayPercentScale
    );
    setValue('backgroundOverlayOriginX', values.backgroundOverlayOriginX);
    setValue('backgroundOverlayOriginY', values.backgroundOverlayOriginY);
    setValue('backgroundColor', values.backgroundColor);
    setValue('textColor', values.textColor);
    setValue('bodyTextColor', values.bodyTextColor);
    setValue('bodyTextAlignment', values.bodyTextAlignment);
    setValue('backgroundImageUrl', values.backgroundImageUrl);
    setValue('backgroundAlpha', values.backgroundAlpha);
    setValue('backgroundImageOriginX', values.backgroundImageOriginX);
    setValue('backgroundImageOriginY', values.backgroundImageOriginY);
    setValue('backgroundImagePercentScale', values.backgroundImagePercentScale);
    setValue('textOriginX', values.textOriginX);
    setValue('textOriginY', values.textOriginY);
    setValue('textWidth', values.textWidth);
    setValue('logoPercentScale', values.logoPercentScale);
    setValue('useDefaultLogoPosition', values.useDefaultLogoPosition);
    setValue('logoOriginX', values.logoOriginX);
    setValue('logoOriginY', values.logoOriginY);
    setValue('showLogo', values.showLogo);
    setValue('titleTextSize', values.titleTextSize);
    setValue('titleSizePt', values.titleSizePt);
    setValue('includeFooter', values.includeFooter);
  }, [
    existingConfig,
    effectiveCoverConfig,
    effectiveDividerConfig,
    watchedInherit,
    isCover,
    reset,
    setValue,
    resolveDividerVisualSource,
  ]);

  // Mutations — refetch TenantInformation after saving so the data is
  // fresh across the site
  const [updateCover] = useUpdateCoverSlideConfigMutation({
    onError: createErrorFeedback(
      'Failed to save cover slide configuration. Please try again.'
    ),
    onCompleted: createSuccessFeedback(
      'Cover slide configuration saved successfully.'
    ),
    refetchQueries: [TenantInformationDocument],
    awaitRefetchQueries: true,
    // Override the global onQueryUpdated which blocks TenantInformation
    // refetches (it's in IGNORED_REFETCH_DOCUMENTS).
    onQueryUpdated: () => true,
  });
  const [updateDivider] = useUpdateDividerSlideConfigMutation({
    onError: createErrorFeedback(
      'Failed to save divider slide configuration. Please try again.'
    ),
    onCompleted: createSuccessFeedback(
      'Divider slide configuration saved successfully.'
    ),
    refetchQueries: [TenantInformationDocument],
    awaitRefetchQueries: true,
    onQueryUpdated: () => true,
  });

  const onSubmit = handleSubmit(async (values) => {
    const input = toMutationInput(values, variant);
    const variables = { input, subBrandID: selectedSubBrandId };
    if (isCover) {
      await updateCover({ variables });
    } else {
      await updateDivider({ variables });
    }
  });

  return (
    <FormProvider {...formMethods}>
      <SlideConfigFormInner
        key={`${variant}:${selectedSubBrandId ?? 'default'}`}
        variant={variant}
        onSubmit={onSubmit}
      />
    </FormProvider>
  );
}
